import { useEffect, useState } from "react";
import { getCategories, joinProducer } from "@/lib/s2tApi";
import { CheckCircle2, Loader2 } from "lucide-react";

const ALCOHOL_CATEGORIES = ["wineries", "craft_brewery"];

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-[#2F4829] mb-1.5">
      {label} {required && <span className="text-[#C86D51]">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-[#D8E0D5] bg-white px-4 py-3 text-[#1C241B] outline-none focus:border-[#4A6B41] focus:ring-2 focus:ring-[#4A6B41]/20";

export const ProducerForm = ({ onOpenLegal }) => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    business_name: "",
    owner_name: "",
    email: "",
    phone: "",
    city: "",
    state_province: "",
    postal_code: "",
    street_address: "",
    category: "",
    products_offered: "",
    website_url: "",
    social_links: "",
    approx_num_products: "",
    sells_direct: false,
    pickup_or_delivery: "",
    permission_granted: false,
  });
  const [ageAck, setAgeAck] = useState(false);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    getCategories()
      .then((c) => setCategories(Array.isArray(c) ? c : []))
      .catch(() => setCategories([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isAlcohol = ALCOHOL_CATEGORIES.includes(form.category);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const required = [
      ["business_name", "Business name"],
      ["owner_name", "Your name"],
      ["email", "Email"],
      ["phone", "Phone"],
      ["city", "City"],
      ["state_province", "Province / State"],
      ["category", "Category"],
    ];
    for (const [k, label] of required) {
      if (!String(form[k]).trim()) {
        setError(`${label} is required.`);
        return;
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!form.permission_granted) {
      setError("Please grant permission for us to contact you.");
      return;
    }
    if (isAlcohol && !ageAck) {
      setError("Please confirm the 19+ alcohol acknowledgment.");
      return;
    }
    setState("loading");
    try {
      await joinProducer({ ...form, photo_urls: [] });
      setState("done");
    } catch (err) {
      setState("error");
      setError(err.message);
    }
  };

  if (state === "done") {
    return (
      <div data-testid="producer-success" className="text-center py-8">
        <CheckCircle2 size={48} className="mx-auto text-[#4A6B41]" />
        <h3 className="mt-4 font-sans font-extrabold text-2xl text-[#1C241B]">
          Application received!
        </h3>
        <p className="mt-2 text-[#5A6757] max-w-md mx-auto">
          Thanks for your interest in joining Source2Table as a producer. Our team
          will review your details and reach out with next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="producer-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Business / Farm name" required>
          <input data-testid="producer-business-name" className={inputCls}
            value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
        </Field>
        <Field label="Your name" required>
          <input data-testid="producer-owner-name" className={inputCls}
            value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} />
        </Field>
        <Field label="Email" required>
          <input data-testid="producer-email" type="email" className={inputCls}
            value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label="Phone" required>
          <input data-testid="producer-phone" className={inputCls}
            value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field label="City" required>
          <input data-testid="producer-city" className={inputCls}
            value={form.city} onChange={(e) => set("city", e.target.value)} />
        </Field>
        <Field label="Province / State" required>
          <input data-testid="producer-state" className={inputCls} placeholder="e.g. British Columbia"
            value={form.state_province} onChange={(e) => set("state_province", e.target.value)} />
        </Field>
        <Field label="Postal code">
          <input data-testid="producer-postal" className={inputCls}
            value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
        </Field>
        <Field label="Street address">
          <input data-testid="producer-street" className={inputCls}
            value={form.street_address} onChange={(e) => set("street_address", e.target.value)} />
        </Field>
      </div>

      <Field label="Primary category" required>
        <select data-testid="producer-category" className={inputCls}
          value={form.category} onChange={(e) => set("category", e.target.value)}>
          <option value="">Select a category…</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </Field>

      {isAlcohol && (
        <div data-testid="alcohol-notice" className="rounded-xl border border-[#C86D51]/40 bg-[#C86D51]/10 p-4">
          <p className="text-sm text-[#7a3a28] leading-relaxed">
            Alcohol producers (wineries &amp; craft breweries) and their listings
            are restricted to adults 19+ and are subject to applicable local
            liquor laws. Listing requires valid licensing.
          </p>
          <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
            <input data-testid="producer-age-ack" type="checkbox" className="mt-1"
              checked={ageAck} onChange={(e) => setAgeAck(e.target.checked)} />
            <span className="text-sm text-[#2F4829]">
              I confirm I am 19+ and will comply with all applicable liquor laws
              and licensing requirements.
            </span>
          </label>
        </div>
      )}

      <Field label="What products do you offer?">
        <textarea data-testid="producer-products" rows={3} className={inputCls}
          placeholder="e.g. Heirloom tomatoes, free-range eggs, raw honey…"
          value={form.products_offered} onChange={(e) => set("products_offered", e.target.value)} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Website">
          <input data-testid="producer-website" className={inputCls} placeholder="https://"
            value={form.website_url} onChange={(e) => set("website_url", e.target.value)} />
        </Field>
        <Field label="Social links">
          <input data-testid="producer-social" className={inputCls} placeholder="Instagram, Facebook…"
            value={form.social_links} onChange={(e) => set("social_links", e.target.value)} />
        </Field>
        <Field label="Approx. number of products">
          <select data-testid="producer-numproducts" className={inputCls}
            value={form.approx_num_products} onChange={(e) => set("approx_num_products", e.target.value)}>
            <option value="">Select…</option>
            <option>1-5</option><option>6-20</option><option>21-50</option><option>50+</option>
          </select>
        </Field>
        <Field label="Pickup or delivery?">
          <select data-testid="producer-fulfilment" className={inputCls}
            value={form.pickup_or_delivery} onChange={(e) => set("pickup_or_delivery", e.target.value)}>
            <option value="">Select…</option>
            <option>Pickup</option><option>Delivery</option><option>Both</option>
          </select>
        </Field>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input data-testid="producer-sells-direct" type="checkbox" className="mt-1"
          checked={form.sells_direct} onChange={(e) => set("sells_direct", e.target.checked)} />
        <span className="text-sm text-[#2F4829]">I sell directly to consumers.</span>
      </label>

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input data-testid="producer-permission" type="checkbox" className="mt-1"
          checked={form.permission_granted} onChange={(e) => set("permission_granted", e.target.checked)} />
        <span className="text-sm text-[#2F4829]">
          I give Source2Table permission to contact me about joining, and I agree to the{" "}
          <button type="button" data-testid="producer-terms-link"
            onClick={() => onOpenLegal("terms_of_service")}
            className="underline text-[#4A6B41] font-semibold">Terms</button>{" "}
          and{" "}
          <button type="button" data-testid="producer-privacy-link"
            onClick={() => onOpenLegal("privacy_policy")}
            className="underline text-[#4A6B41] font-semibold">Privacy Policy</button>.{" "}
          <span className="text-[#C86D51]">*</span>
        </span>
      </label>

      {error && (
        <p data-testid="producer-error" className="text-sm text-red-600">{error}</p>
      )}

      <button data-testid="producer-submit-btn" type="submit" disabled={state === "loading"}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B41] text-[#F9F6F0] px-7 py-3.5 font-sans font-semibold hover:bg-[#2F4829] transition-colors disabled:opacity-60">
        {state === "loading" && <Loader2 size={18} className="animate-spin" />}
        Submit producer application
      </button>
    </form>
  );
};
